import {
  Component,
  forwardRef,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
  HostListener
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * WheelTimePickerComponent - iOS-style wheel time picker
 *
 * Features:
 * - Two vertical scrollable columns: hours (00-23) and minutes (00-59)
 * - Smooth drag to select time with physics-based momentum
 * - Fixed center highlight area showing selected value
 * - Items above and below appear faded
 * - Smooth snapping with momentum
 * - ControlValueAccessor implementation for reactive forms
 * - Supports configurable minute step (default: 1)
 * - Auto-selects when drag ends (center item)
 * - Format: HH : MM with centered separator
 * - Uses Pointer Events for cross-browser drag support
 *
 * Pointer Events approach:
 * - Uses setPointerCapture to ensure pointer events are captured by the element
 * - Works identically on mouse, touch, and pen
 * - No document-level listeners needed
 * - Safari-compatible without fighting browser scroll handling
 */
@Component({
  selector: 'usb-wheel-time-picker',
  templateUrl: './wheel-time-picker.component.html',
  styleUrls: ['./wheel-time-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WheelTimePickerComponent),
      multi: true
    }
  ]
})
export class WheelTimePickerComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
  @ViewChild('hoursColumn') hoursColumn!: ElementRef<HTMLElement>;
  @ViewChild('minutesColumn') minutesColumn!: ElementRef<HTMLElement>;

  @Input() initialValue: string = '14:30';
  @Input() minuteStep: number = 1;
  @Input() disabled: boolean = false;

  hours: string[] = [];
  minutes: string[] = [];

  selectedHour: string = '00';
  selectedMinute: string = '00';

  // Drag state
  private isDragging = false;
  private dragColumn: HTMLElement | null = null;
  private dragType: 'hour' | 'minute' | null = null;
  private lastY = 0;
  private lastTime = 0;
  private velocity = 0;

  // Layout constants - MUST match CSS values
  // CSS: .wheel-item height: 22px, .wheel-column height: 110px
  private readonly itemHeight = 22;
  private readonly columnHeight = 110;

  // Momentum animation
  private momentumId: number | null = null;

  // ControlValueAccessor callbacks
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.generateHours();
    this.generateMinutes();
    this.parseInitialValue();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.scrollToValue(this.selectedHour, this.selectedMinute);
    }, 0);
  }

  ngOnDestroy(): void {
    this.cancelMomentum();
  }

  private generateHours(): void {
    this.hours = [];
    for (let i = 0; i < 24; i++) {
      this.hours.push(i.toString().padStart(2, '0'));
    }
  }

  private generateMinutes(): void {
    this.minutes = [];
    for (let i = 0; i < 60; i += this.minuteStep) {
      this.minutes.push(i.toString().padStart(2, '0'));
    }
  }

  private parseInitialValue(): void {
    if (this.initialValue && this.initialValue.includes(':')) {
      const [hour, minute] = this.initialValue.split(':');
      this.selectedHour = hour.padStart(2, '0');
      const minNum = parseInt(minute, 10);
      const closestMinute = Math.round(minNum / this.minuteStep) * this.minuteStep;
      this.selectedMinute = (closestMinute % 60).toString().padStart(2, '0');
    }
  }

  // ========== SCROLL HELPERS ==========

  /**
   * The padding-top on .wheel-items offsets items so the first item
   * is centered in the highlight at scrollTop = 0.
   * To keep item[i] centered, scrollTop = i * itemHeight.
   */
  private scrollToValue(hour: string, minute: string): void {
    if (this.hoursColumn?.nativeElement) {
      const hourIndex = this.hours.indexOf(hour);
      if (hourIndex >= 0) {
        this.hoursColumn.nativeElement.scrollTop = hourIndex * this.itemHeight;
      }
    }
    if (this.minutesColumn?.nativeElement) {
      const minuteIndex = this.minutes.indexOf(minute);
      if (minuteIndex >= 0) {
        this.minutesColumn.nativeElement.scrollTop = minuteIndex * this.itemHeight;
      }
    }
  }

  /**
   * Given the current scrollTop, determine which item index is closest
   * to the center highlight.
   */
  private getItemIndexFromScroll(scrollTop: number): number {
    let index = Math.round(scrollTop / this.itemHeight);
    return Math.max(0, Math.min(index, 23)); // 24 hours
  }

  private getMinuteIndexFromScroll(scrollTop: number): number {
    const totalMinutes = Math.ceil(60 / this.minuteStep);
    let index = Math.round(scrollTop / this.itemHeight);
    return Math.max(0, Math.min(index, totalMinutes - 1));
  }

  // ========== POINTER EVENT HANDLERS ==========

  onPointerDown(event: PointerEvent, column: HTMLElement, type: 'hour' | 'minute'): void {
    event.preventDefault();
    (event.target as HTMLElement)?.setPointerCapture?.(event.pointerId);

    this.cancelMomentum();
    this.isDragging = true;
    this.dragColumn = column;
    this.dragType = type;
    this.lastY = event.clientY;
    this.velocity = 0;
    this.lastTime = Date.now();
  }

  onPointerMove(event: PointerEvent, column: HTMLElement): void {
    if (!this.isDragging || this.dragColumn !== column) return;

    event.preventDefault();

    const currentY = event.clientY;
    const currentTime = Date.now();
    const deltaTime = currentTime - this.lastTime;
    const deltaY = currentY - this.lastY;

    // Track velocity (pixels/ms)
    if (deltaTime > 0) {
      this.velocity = deltaY / deltaTime;
    }

    // Direct 1:1 mapping: finger moves down → scrollTop decreases (content moves up)
    let newScrollTop = column.scrollTop - deltaY;

    // Rubber band effect at edges
    const maxScroll = column.scrollHeight - column.clientHeight;

    if (newScrollTop < 0) {
      newScrollTop = newScrollTop * 0.3;
    } else if (newScrollTop > maxScroll) {
      const overflow = newScrollTop - maxScroll;
      newScrollTop = maxScroll + overflow * 0.3;
    }

    column.scrollTop = newScrollTop;
    this.lastY = currentY;
    this.lastTime = currentTime;
  }

  onPointerUp(event: PointerEvent, column: HTMLElement, type: 'hour' | 'minute'): void {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.dragColumn = null;
    this.dragType = null;

    this.startMomentum(column, type);
  }

  onPointerCancel(event: PointerEvent, column: HTMLElement, type: 'hour' | 'minute'): void {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.dragColumn = null;
    this.dragType = null;

    this.snapToNearest(column, type);
  }

  // ========== WHEEL EVENT (mouse wheel / trackpad) ==========

  onWheel(event: WheelEvent, column: HTMLElement, type: 'hour' | 'minute'): void {
    event.preventDefault();
    event.stopPropagation();

    this.cancelMomentum();

    const delta = event.deltaY > 0 ? this.itemHeight : -this.itemHeight;
    const maxScroll = column.scrollHeight - column.clientHeight;
    const newScrollTop = Math.max(0, Math.min(column.scrollTop + delta, maxScroll));

    column.scrollTop = newScrollTop;

    // Snap after wheel - immediate snap for better UX
    this.snapToNearest(column, type);
  }

  // ========== MOMENTUM ==========

  private cancelMomentum(): void {
    if (this.momentumId !== null) {
      cancelAnimationFrame(this.momentumId);
      this.momentumId = null;
    }
  }

  private startMomentum(column: HTMLElement, type: 'hour' | 'minute'): void {
    // If velocity is negligible, snap directly
    if (Math.abs(this.velocity) < 0.05) {
      this.snapToNearest(column, type);
      return;
    }

    // Convert velocity from px/ms to px/frame (at ~60fps)
    let currentVelocity = this.velocity * 16.67;
    // Negative because: positive velocity = finger moved DOWN = content scrolls UP = scrollTop decreases
    currentVelocity = -currentVelocity;

    let currentScrollTop = column.scrollTop;

    const friction = 0.94;
    const minVelocity = 0.3;

    const animate = () => {
      currentVelocity *= friction;

      if (Math.abs(currentVelocity) < minVelocity) {
        this.momentumId = null;
        this.snapToNearest(column, type);
        return;
      }

      currentScrollTop += currentVelocity;

      // Rubber band at edges during momentum
      const maxScroll = column.scrollHeight - column.clientHeight;
      if (currentScrollTop < 0) {
        currentScrollTop *= 0.5;
        currentVelocity *= 0.5;
      } else if (currentScrollTop > maxScroll) {
        const overflow = currentScrollTop - maxScroll;
        currentScrollTop = maxScroll + overflow * 0.5;
        currentVelocity *= 0.5;
      }

      column.scrollTop = currentScrollTop;
      this.momentumId = requestAnimationFrame(animate);
    };

    this.momentumId = requestAnimationFrame(animate);
  }

  // ========== SNAPPING ==========

  private snapToNearest(column: HTMLElement, type: 'hour' | 'minute'): void {
    const items = type === 'hour' ? this.hours : this.minutes;
    const itemIndex = type === 'hour'
      ? this.getItemIndexFromScroll(column.scrollTop)
      : this.getMinuteIndexFromScroll(column.scrollTop);

    const targetScroll = itemIndex * this.itemHeight;

    // Animate to snap position
    this.animateScrollTo(column, targetScroll, 200);

    // Update selection
    const newValue = items[itemIndex];
    if (type === 'hour' && newValue !== this.selectedHour) {
      this.selectedHour = newValue;
      this.emitValue();
    } else if (type === 'minute' && newValue !== this.selectedMinute) {
      this.selectedMinute = newValue;
      this.emitValue();
    }
  }

  private animateScrollTo(element: HTMLElement, target: number, duration: number): void {
    const start = element.scrollTop;
    const change = target - start;

    // Already at target
    if (Math.abs(change) < 0.5) {
      element.scrollTop = target;
      return;
    }

    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      element.scrollTop = start + change * eased;

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  // ========== HELPERS ==========

  private emitValue(): void {
    const value = `${this.selectedHour}:${this.selectedMinute}`;
    this.onChange(value);
    this.onTouched();
  }

  // ========== ControlValueAccessor ==========

  writeValue(value: string): void {
    if (value && value.includes(':')) {
      const [hour, minute] = value.split(':');
      this.selectedHour = hour.padStart(2, '0');
      const minNum = parseInt(minute, 10);
      const closestMinute = Math.round(minNum / this.minuteStep) * this.minuteStep;
      this.selectedMinute = (closestMinute % 60).toString().padStart(2, '0');
      setTimeout(() => {
        this.scrollToValue(this.selectedHour, this.selectedMinute);
      }, 0);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  isSelectedHour(hour: string): boolean {
    return hour === this.selectedHour;
  }

  isSelectedMinute(minute: string): boolean {
    return minute === this.selectedMinute;
  }
}
